import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Search, Edit, Trash2, Settings, QrCode, X, CalendarCheck, Zap, RefreshCw, Filter, CheckCircle2, AlertCircle, BadgeCheck } from 'lucide-react';
import api from '../services/api';
import ClientManageModal from '../components/ClientManageModal';
import ClientBadgeModal from '../components/ClientBadgeModal';

const Clients = () => {
    const [clients, setClients] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'abonnes' | 'seances' | 'expires'>('all');

    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        gender: 'Homme',
        email: '',
        phone: '',
        date_of_birth: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [managingClient, setManagingClient] = useState<any>(null);
    const [editingClientId, setEditingClientId] = useState<number | null>(null);
    const [showQrCodeClient, setShowQrCodeClient] = useState<any>(null);
    const [badgeClient, setBadgeClient] = useState<any>(null);

    const resetForm = () => {
        setFormData({ first_name: '', last_name: '', gender: 'Homme', email: '', phone: '', date_of_birth: '' });
        setEditingClientId(null);
    };

    const handleOpenCreateModal = () => {
        resetForm();
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (client: any) => {
        setFormData({
            first_name: client.first_name || '',
            last_name: client.last_name || '',
            gender: client.gender || 'Homme',
            email: client.email || '',
            phone: client.phone || '',
            date_of_birth: client.date_of_birth ? client.date_of_birth.substring(0, 10) : ''
        });
        setEditingClientId(client.id);
        setIsModalOpen(true);
    };

    const fetchClients = async () => {
        try {
            setLoading(true);
            const response = await api.get('/clients');
            setClients(response.data);
        } catch (error) {
            console.error("Erreur lors de la récupération des clients", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClients();
    }, []);

    const filteredClients = useMemo(() => {
        return clients.filter(c => {
            const matchesSearch =
                !searchTerm ||
                c.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                c.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (c.phone && c.phone.includes(searchTerm)) ||
                (c.subscription_forfait_name && c.subscription_forfait_name.toLowerCase().includes(searchTerm.toLowerCase()));

            const matchesStatus =
                statusFilter === 'all' ||
                (statusFilter === 'abonnes' && c.subscription_status === 'actif') ||
                (statusFilter === 'seances' && Number(c.valide_seances) > 0) ||
                (statusFilter === 'expires' && c.subscription_status === 'expire');

            return matchesSearch && matchesStatus;
        });
    }, [clients, searchTerm, statusFilter]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            if (editingClientId) {
                await api.put(`/clients/${editingClientId}`, formData);
            } else {
                await api.post('/clients', formData);
            }
            setIsModalOpen(false);
            resetForm();
            fetchClients(); // Rafraîchir la liste
        } catch (error) {
            console.error("Erreur lors de la sauvegarde du client", error);
            alert("Erreur lors de la sauvegarde");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="w-full">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-brand-dark">Gestion des Clients</h1>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Suivi en temps réel des clients, abonnements actifs et séances
                    </p>
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                    <button
                        type="button"
                        onClick={fetchClients}
                        disabled={loading}
                        className="flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-md shadow-xs transition-colors"
                        title="Actualiser la liste"
                    >
                        <RefreshCw size={16} className={loading ? 'animate-spin text-brand-primary' : ''} />
                        <span className="hidden sm:inline">Actualiser</span>
                    </button>
                    <button
                        onClick={handleOpenCreateModal}
                        className="flex items-center gap-2 px-4 py-2 bg-brand-primary hover:bg-brand-accent text-brand-dark font-medium rounded-md transition-colors w-full sm:w-auto justify-center"
                    >
                        <Plus size={18} />
                        Nouveau Client
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                {/* Search & Filter Bar */}
                <div className="p-4 border-b border-gray-100 flex flex-col md:flex-row justify-between items-stretch md:items-center gap-3 bg-gray-50/50">
                    <div className="relative w-full md:w-80">
                        <input
                            type="text"
                            placeholder="Rechercher par nom, tél, forfait..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-brand-primary focus:border-brand-primary outline-none text-sm"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                    </div>

                    {/* Filter tabs */}
                    <div className="flex flex-wrap items-center gap-1.5 self-start md:self-auto">
                        <span className="text-xs text-gray-500 font-medium mr-1 flex items-center gap-1">
                            <Filter size={14} /> Filtre :
                        </span>
                        <button
                            type="button"
                            onClick={() => setStatusFilter('all')}
                            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${statusFilter === 'all'
                                ? 'bg-brand-primary text-brand-dark shadow-xs'
                                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                                }`}
                        >
                            Tous ({clients.length})
                        </button>
                        <button
                            type="button"
                            onClick={() => setStatusFilter('abonnes')}
                            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${statusFilter === 'abonnes'
                                ? 'bg-emerald-600 text-white shadow-xs'
                                : 'bg-white text-gray-600 hover:bg-emerald-50 hover:text-emerald-700 border border-gray-200'
                                }`}
                        >
                            Abonnés Actifs
                        </button>
                        <button
                            type="button"
                            onClick={() => setStatusFilter('seances')}
                            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${statusFilter === 'seances'
                                ? 'bg-blue-600 text-white shadow-xs'
                                : 'bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-700 border border-gray-200'
                                }`}
                        >
                            Séances Valides
                        </button>
                        <button
                            type="button"
                            onClick={() => setStatusFilter('expires')}
                            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${statusFilter === 'expires'
                                ? 'bg-amber-600 text-white shadow-xs'
                                : 'bg-white text-gray-600 hover:bg-amber-50 hover:text-amber-700 border border-gray-200'
                                }`}
                        >
                            Expirés
                        </button>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs text-gray-500 uppercase bg-gray-50 border-b border-gray-100">
                            <tr>
                                <th className="px-5 py-4 font-medium">Nom & Prénom</th>
                                <th className="px-5 py-4 font-medium">Contact</th>
                                <th className="px-5 py-4 font-medium">Abonnement Actuel</th>
                                <th className="px-5 py-4 font-medium">Séances</th>
                                <th className="px-5 py-4 font-medium">Statut</th>
                                <th className="px-5 py-4 font-medium">Date d'inscription</th>
                                <th className="px-5 py-4 font-medium text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                                        Chargement des clients...
                                    </td>
                                </tr>
                            ) : filteredClients.length > 0 ? (
                                filteredClients.map((client) => {
                                    const isSubActive = client.subscription_status === 'actif';
                                    const isSubExpired = client.subscription_status === 'expire';
                                    const hasValidSeances = Number(client.valide_seances) > 0;

                                    return (
                                        <tr key={client.id} className="hover:bg-gray-50/50 transition-colors">
                                            {/* NOM & PRÉNOM */}
                                            <td className="px-5 py-4">
                                                <div className="font-semibold text-gray-900 flex items-center gap-2">
                                                    <span>{client.first_name} {client.last_name}</span>
                                                    {client.gender && (
                                                        <span className="text-[10px] px-1.5 py-0.2 bg-gray-100 text-gray-600 rounded">
                                                            {client.gender}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* CONTACT */}
                                            <td className="px-5 py-4 text-gray-600">
                                                <div className="text-xs font-mono">{client.phone || '-'}</div>
                                                <div className="text-[11px] text-gray-400">{client.email || ''}</div>
                                            </td>

                                            {/* ABONNEMENT ACTUEL */}
                                            <td className="px-5 py-4">
                                                {client.subscription_forfait_name ? (
                                                    <div className="space-y-0.5">
                                                        <div className="font-semibold text-gray-900 flex items-center gap-1.5 text-xs">
                                                            <CalendarCheck size={14} className="text-blue-600 shrink-0" />
                                                            <span className="truncate max-w-[180px]" title={client.subscription_forfait_name}>
                                                                {client.subscription_forfait_name}
                                                            </span>
                                                        </div>
                                                        {isSubActive ? (
                                                            <div className="text-[11px] text-emerald-700 font-medium">
                                                                Expire le {new Date(client.subscription_end_date).toLocaleDateString('fr-FR')}
                                                                {client.subscription_days_remaining !== null && (
                                                                    <span className="ml-1 text-[10px] bg-emerald-50 text-emerald-800 px-1 py-0.2 rounded font-bold">
                                                                        {client.subscription_days_remaining}j restants
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ) : isSubExpired ? (
                                                            <div className="text-[11px] text-amber-700 font-medium">
                                                                Expiré le {new Date(client.subscription_end_date).toLocaleDateString('fr-FR')}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-400 italic">Aucun abonnement</span>
                                                )}
                                            </td>

                                            {/* SÉANCES */}
                                            <td className="px-5 py-4">
                                                {hasValidSeances ? (
                                                    <div className="space-y-0.5">
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                                                            <Zap size={11} />
                                                            {client.valide_seances} séance{Number(client.valide_seances) > 1 ? 's' : ''} valide{Number(client.valide_seances) > 1 ? 's' : ''}
                                                        </span>
                                                        {client.latest_seance_date && (
                                                            <div className="text-[10px] text-gray-500">
                                                                Le {new Date(client.latest_seance_date).toLocaleDateString('fr-FR')} à {client.latest_seance_time?.substring(0, 5)}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : Number(client.total_seances) > 0 ? (
                                                    <div className="space-y-0.5">
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700">
                                                            {client.total_seances} consommée{Number(client.total_seances) > 1 ? 's' : ''}
                                                        </span>
                                                        {client.latest_seance_date && (
                                                            <div className="text-[10px] text-gray-400">
                                                                Dernière le {new Date(client.latest_seance_date).toLocaleDateString('fr-FR')}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-400 italic">-</span>
                                                )}
                                            </td>

                                            {/* STATUT */}
                                            <td className="px-5 py-4">
                                                {isSubActive ? (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
                                                        <CheckCircle2 size={12} />
                                                        Abonné
                                                    </span>
                                                ) : hasValidSeances ? (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                                                        <Zap size={12} />
                                                        Séance active
                                                    </span>
                                                ) : isSubExpired ? (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800">
                                                        <AlertCircle size={12} />
                                                        Expiré
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                                        Inscrit
                                                    </span>
                                                )}
                                            </td>

                                            {/* DATE INSCRIPTION */}
                                            <td className="px-5 py-4 text-gray-600 text-xs">
                                                {client.last_inscription_date
                                                    ? new Date(client.last_inscription_date).toLocaleDateString('fr-FR')
                                                    : client.created_at ? new Date(client.created_at).toLocaleDateString('fr-FR') : '-'}
                                            </td>

                                            {/* ACTIONS */}
                                            <td className="px-5 py-4 text-right whitespace-nowrap">
                                                <button
                                                    onClick={() => setManagingClient(client)}
                                                    className="text-brand-primary hover:text-blue-700 p-1.5 bg-blue-50 rounded transition-colors mr-1.5"
                                                    title="Gérer le client (Abonnement)"
                                                >
                                                    <Settings size={18} />
                                                </button>
                                                <button
                                                    onClick={() => setBadgeClient({
                                                        id: client.id,
                                                        first_name: client.first_name,
                                                        last_name: client.last_name,
                                                        phone: client.phone,
                                                        card_number: client.card_number,
                                                        photo_url: client.photo_url
                                                    })}
                                                    className="text-amber-600 hover:text-amber-800 p-1.5 bg-amber-50 rounded transition-colors mr-1.5"
                                                    title="Imprimer le badge membre"
                                                >
                                                    <BadgeCheck size={18} />
                                                </button>
                                                {/* <button
                                                    onClick={() => setShowQrCodeClient(client)}
                                                    className="text-gray-500 hover:text-gray-800 p-1.5 bg-gray-100 rounded transition-colors mr-1.5"
                                                    title="Afficher le QR Code"
                                                >
                                                    <QrCode size={17} />
                                                </button> */}
                                                <button
                                                    type="button"
                                                    onClick={() => handleOpenEditModal(client)}
                                                    className="text-gray-400 hover:text-gray-600 p-1.5 transition-colors mr-1"
                                                    title="Modifier"
                                                >
                                                    <Edit size={18} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={async () => {
                                                        if (window.confirm(`Êtes-vous sûr de vouloir désactiver le client ${client.first_name} ${client.last_name} ?`)) {
                                                            try {
                                                                await api.delete(`/clients/${client.id}`);
                                                                fetchClients();
                                                            } catch (err) {
                                                                console.error(err);
                                                                alert("Erreur lors de la désactivation du client.");
                                                            }
                                                        }
                                                    }}
                                                    className="text-gray-400 hover:text-red-500 p-1.5 transition-colors"
                                                    title="Désactiver"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                                        Aucun client trouvé.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modale d'ajout de client */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-gray-900">{editingClientId ? 'Modifier le Client' : 'Nouveau Client'}</h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600">×</button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                                    <input
                                        type="text" required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand-primary focus:border-brand-primary outline-none"
                                        value={formData.first_name}
                                        onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                                    <input
                                        type="text" required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand-primary focus:border-brand-primary outline-none"
                                        value={formData.last_name}
                                        onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input
                                    type="email"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand-primary focus:border-brand-primary outline-none"
                                    value={formData.email}
                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                                <input
                                    type="tel"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand-primary focus:border-brand-primary outline-none"
                                    value={formData.phone}
                                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label>
                                <input
                                    type="date"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand-primary focus:border-brand-primary outline-none"
                                    value={formData.date_of_birth}
                                    onChange={e => setFormData({ ...formData, date_of_birth: e.target.value })}
                                />
                            </div>
                            <div className="pt-4 flex justify-end gap-3">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-medium transition-colors">Annuler</button>
                                <button type="submit" disabled={isSubmitting} className={`px-4 py-2 text-brand-dark bg-brand-primary hover:bg-brand-accent rounded-md font-medium transition-colors ${isSubmitting ? 'opacity-70' : ''}`}>
                                    {isSubmitting ? 'Sauvegarde...' : (editingClientId ? 'Modifier' : 'Créer')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {managingClient && (
                <ClientManageModal
                    client={managingClient}
                    onClose={() => {
                        setManagingClient(null);
                        fetchClients();
                    }}
                    onUpdated={fetchClients}
                />
            )}

            {/* Modale Badge Membre */}
            {badgeClient && (
                <ClientBadgeModal
                    isOpen={!!badgeClient}
                    onClose={() => setBadgeClient(null)}
                    client={badgeClient}
                />
            )}

            {/* Modale QR Code */}
            {showQrCodeClient && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full text-center relative">
                        <button
                            onClick={() => setShowQrCodeClient(null)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 bg-gray-100 rounded-full p-1"
                        >
                            <X size={20} />
                        </button>
                        <h3 className="text-2xl font-black text-brand-dark mb-1">Carte Membre</h3>
                        <p className="text-gray-600 font-medium mb-6">{showQrCodeClient.first_name} {showQrCodeClient.last_name}</p>

                        <div className="bg-white p-4 inline-block rounded-xl border-4 border-brand-primary shadow-sm mb-6">
                            <img
                                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${showQrCodeClient.id}`}
                                alt={`QR Code ${showQrCodeClient.first_name}`}
                                className="w-48 h-48"
                            />
                        </div>

                        <div className="bg-gray-50 rounded-lg py-3">
                            <p className="text-sm text-gray-500 uppercase font-bold tracking-widest">ID Client</p>
                            <p className="text-xl font-black font-mono text-brand-dark">{showQrCodeClient.id}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Clients;
